import { useEffect, useRef } from "react";
import { Trash2 } from "lucide-react";
import {
  ReportExtractionItemType,
  ReportExtractionItemCitationType,
} from "../../../models/reportExtraction";

interface ReportExtractionCardsProps {
  items: ReportExtractionItemType[];
  onItemClick: (item: ReportExtractionItemType, index: number) => void;
  onCitationClick: (citation: ReportExtractionItemCitationType) => void;
  onDeleteItem: (itemId: string) => Promise<void>;
  selectedIndex: number | null;
}

export const ReportExtractionCards = ({
  items,
  onItemClick,
  onCitationClick,
  onDeleteItem,
  selectedIndex,
}: ReportExtractionCardsProps) => {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Scroll selected card into view (centered)
  useEffect(() => {
    if (selectedIndex !== null && cardRefs.current[selectedIndex]) {
      cardRefs.current[selectedIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedIndex]);

  if (items.length === 0) {
    return (
      <div className="text-center text-gray-500 mt-8">
        No extracted items found.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {items.map((item, index) => (
        <div
          key={item.id}
          ref={(el) => {
            cardRefs.current[index] = el;
          }}
          className={`border rounded-lg p-4 hover:border-stone-300 cursor-pointer relative ${
            selectedIndex === index
              ? "border-blue-500 ring-2 ring-blue-500 bg-blue-50"
              : "border-stone-200"
          }`}
          onClick={() => onItemClick(item, index)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              onItemClick(item, index);
            }
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteItem(item.id);
            }}
            className="absolute top-3 right-3 p-1.5 hover:bg-red-50 rounded text-gray-400 hover:text-red-600"
            title="Delete item"
          >
            <Trash2 size={16} />
          </button>
          <div className="grid grid-cols-2 gap-2 pr-8">
            {Object.entries(item.data).map(([key, value]) => (
              <div key={key} className="flex flex-col">
                <span className="text-xs text-gray-500 font-medium">{key}</span>
                <span className="text-sm">
                  {typeof value === "object" && value !== null
                    ? JSON.stringify(value)
                    : String(value ?? "")}
                </span>
              </div>
            ))}
          </div>

          {item.citations.length > 0 && (
            <div className="mt-3 pt-3 border-t border-stone-100">
              <span className="text-xs text-gray-500">Citations:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {item.citations.map((citation) => (
                  <button
                    key={citation.id}
                    className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCitationClick(citation);
                    }}
                  >
                    {citation.pageNumber !== null
                      ? `Page ${citation.pageNumber}`
                      : "Citation"}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
